import { useHistory, useParams } from 'react-router';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { putInsurancePolicyPackage } from '@parties/src/features/detail/api/insurance-policies/putInsurancePolicyPackageForParty';
import { putInsurancePolicyForSimulation } from '@flows/src/api/anva-flows/simulation/putInsurancePolicyForSimulation';
import { v4 } from 'uuid';
import cogoToast from 'cogo-toast';
import {
  AnvaQuotesCompletePayload,
  completeConversation,
} from '@conversations/src/features/detail/shared/api/completeConversation';
import { IdResponse } from '../../../../types/API';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import {
  getNonMaatwerkPackages,
  getNonMaatwerkInsurances,
} from '@flows/src/utils/basketCalculations';

const useFinishBasket = (
  sessionId: string,
  setIsCreating: {
    on: () => void;
    off: () => void;
    toggle: () => void;
  },
) => {
  const { flowId } = useParams<{ flowId: string }>();
  const { data: sessionData } = useFlowsSession(sessionId);
  const partyId = sessionData?.prospect?.id || '';
  const history = useHistory();

  // Filter out maatwerk items - only non-maatwerk items should be sent to finalization
  const nonMaatwerkPackages = getNonMaatwerkPackages();
  const nonMaatwerkInsurances = getNonMaatwerkInsurances();

  const createInsurancePoliciesForPackages = async (
    _completionPayload: AnvaQuotesCompletePayload,
  ): Promise<AnvaQuotesCompletePayload> => {
    const completionPayload = [..._completionPayload];
    await Promise.all(
      nonMaatwerkPackages.map((pack) => {
        const calc = async () => {
          const createdPackage = await putInsurancePolicyPackage({
            partyId: partyId,
            insurancePackage: {
              ...pack,
              premium: pack.quotes.reduce(
                (acc: number, { total_premium }: { total_premium: number }) =>
                  acc + total_premium,
                0,
              ),
              name: pack.name,
              anva_package_code: pack.code,
            },
          });
          const quotes = await Promise.all(
            pack.quotes.map((quote) =>
              putInsurancePolicyForSimulation({
                partyId: partyId,
                sessionId: sessionId,
                payload: {
                  name: quote.name,
                  insurance_company: quote.insurance_company.name,
                  coverages: quote.coverages
                    .filter(({ selected }) => selected)
                    .map((c) => ({
                      id: v4(),
                      name: c.name,
                      premium: c.total_premium,
                      external_labels: [
                        {
                          label: c.code,
                          type: 'ANVA',
                        },
                      ],
                    })),
                  documents: [],
                  payment_interval: quote.payment_interval ?? undefined,
                  policy_package_id: createdPackage.id,
                  links: quote.documents.map((doc) => ({
                    filename: doc.name,
                    link: doc.link,
                  })),
                  premium: quote.total_premium,
                  anva_product_id: quote.anva_product_id,
                  anva_collection_method_code:
                    quote.anva_collection_method_code ?? undefined,
                },
              }),
            ),
          );
          (quotes as IdResponse[]).forEach((quote, i) => {
            const selectedMatched = pack.quotes[i];
            if (selectedMatched) {
              completionPayload.push({
                anva_product_id: selectedMatched.anva_product_id,
                index: selectedMatched.index,
                insurance_policy_id: quote.id,
              });
            }
          });
        };
        return calc();
      }),
    );
    return completionPayload;
  };

  const createInsurancePoliciesForIndividualQuotes = async (
    _completionPayload: AnvaQuotesCompletePayload,
  ): Promise<AnvaQuotesCompletePayload> => {
    const completionPayload = [..._completionPayload];
    const quotes = await Promise.all(
      nonMaatwerkInsurances.map((quote) =>
        putInsurancePolicyForSimulation({
          partyId: partyId,
          sessionId: sessionId,
          payload: {
            name: quote.name,
            insurance_company: quote.insurance_company.name,
            coverages: quote.coverages
              .filter(({ selected }) => selected)
              .map((c) => ({
                id: v4(),
                name: c.name,
                premium: c.total_premium,
                external_labels: [
                  {
                    label: c.code,
                    type: 'ANVA',
                  },
                ],
              })),
            documents: [],
            payment_interval: quote.payment_interval ?? undefined,
            links: quote.documents.map((doc) => ({
              filename: doc.name,
              link: doc.link,
            })),
            premium: quote.total_premium,
            anva_product_id: quote.anva_product_id,
            anva_collection_method_code:
              quote.anva_collection_method_code ?? undefined,
          },
        }),
      ),
    );
    (quotes as IdResponse[]).forEach((quote, i) => {
      const selectedMatched = nonMaatwerkInsurances[i];
      if (selectedMatched) {
        completionPayload.push({
          anva_product_id: selectedMatched.anva_product_id,
          index: selectedMatched.index,
          insurance_policy_id: quote.id,
        });
      }
    });
    return completionPayload;
  };

  const completeConversationAndNavigate = async (
    completionPayload: AnvaQuotesCompletePayload,
  ) => {
    await completeConversation({
      conversationId: sessionId,
      anva_quotes: completionPayload,
    });
    history.push({
      pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/selection-overview`,
      search: window.location.search,
    });
  };

  const handleFinish = async () => {
    if (!partyId) return;
    try {
      setIsCreating.on();
      let completionPayload: AnvaQuotesCompletePayload = [];
      completionPayload = await createInsurancePoliciesForPackages(
        completionPayload,
      );
      completionPayload = await createInsurancePoliciesForIndividualQuotes(
        completionPayload,
      );
      completeConversationAndNavigate(completionPayload);
    } catch {
      cogoToast.error('Something went wrong!');
    } finally {
      setIsCreating.off();
    }
  };

  return { handleFinish };
};

export default useFinishBasket;
